import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "cmdk";
import { Inbox, MessageSquarePlus, Sparkles } from "lucide-react";
import { useContextStore } from "@/store/context";
import { builderCalistoHref } from "@/utils/contextUrl";
import {
  filterStudioPaletteCommands,
  studioPaletteNewThreadHref,
  studioPaletteTravailInboxHref,
} from "@/components/studio/studioPaletteCommands";

export default function StudioRunCommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const organizationId = useContextStore((state) => state.org);
  const agentId = useContextStore((state) => state.deploymentId);
  const applicationKey = useContextStore((state) => state.applicationKey);
  const applications = useContextStore((state) => state.applications);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const go = (href: string) => {
    navigate(href);
    setOpen(false);
    setSearch("");
  };

  const reviewApp =
    applications.find((item) => item.key === applicationKey) || applications[0];

  const commands = [
    {
      id: "new-thread",
      label: t("commandPalette.newThread"),
      description: t("commandPalette.newThreadDescription"),
      icon: MessageSquarePlus,
      action: () => {
        if (!organizationId || !agentId) return;
        go(studioPaletteNewThreadHref(organizationId, agentId, applicationKey));
      },
    },
    {
      id: "continue-studio",
      label: t("commandPalette.continueInStudio"),
      description: t("commandPalette.continueInStudioDescription"),
      icon: Sparkles,
      action: () => {
        if (reviewApp) {
          go(`/studio/applications/${reviewApp.id}`);
          return;
        }
        if (!organizationId || !agentId) return;
        go(builderCalistoHref(organizationId, agentId, { applicationKey }));
      },
    },
    {
      id: "travail-inbox",
      label: t("commandPalette.travailInbox"),
      description: t("commandPalette.travailInboxDescription"),
      icon: Inbox,
      action: () =>
        go(
          studioPaletteTravailInboxHref({
            organizationId,
            applicationKey,
            agentId,
          }),
        ),
    },
  ];

  const filtered = filterStudioPaletteCommands(commands, search);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-foreground/40 dark:bg-background/80" />
        <Dialog.Content className="fixed left-1/2 top-[16vh] z-50 w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-background">
          <Dialog.Title className="sr-only">
            {t("commandPalette.title")}
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            {t("commandPalette.description")}
          </Dialog.Description>
          <Command shouldFilter={false} className="flex max-h-[70vh] flex-col">
            <CommandInput
              placeholder={t("commandPalette.placeholder")}
              value={search}
              onValueChange={setSearch}
              className="h-12 border-b border-border bg-transparent px-4 text-sm outline-none"
            />
            <CommandList className="max-h-[calc(70vh-3rem)] overflow-y-auto p-2">
              <CommandEmpty className="px-3 py-6 text-sm text-muted-foreground">
                {t("commandPalette.noCommands")}
              </CommandEmpty>
              <CommandGroup>
                {filtered.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => item.action()}
                    className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 aria-selected:bg-accent aria-selected:text-accent-foreground"
                  >
                    <item.icon className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
